import React from "react";
import { Link } from "react-router-dom";
import {
  beautify,
  createColumn,
  createViewEdit,
  formatDate,
  numberFormat,
  createDeleteAction,
  itemExists,
  useGetUserPermissions,
  objectWithUnsetKeys,
  createSelectItem,
  returnObject,
} from "../../utils/helpers";
import { PaymentStatusTag } from "../../components/common/registerUtils";
import { FormActions } from "../../components/common/FormActions";
import { API_PAYMENTS } from "../../utils/apis";
import {
  CAN_EDIT_PURCHASE_ORDER,
  EXISTING_APPLICANT,
  MIGRATED_APPLICANT,
  NEW_APPLICANT,
  RESTRUCTURED_APPLICANT,
} from "../../utils/constants";

export const usePayments = () => {
  const permissions = useGetUserPermissions();
  const canEditPurchaseOrder = itemExists(permissions, [
    CAN_EDIT_PURCHASE_ORDER,
  ]);
  const tableColumns = () => [
    ...[
      "payment_date",
      "invoice_number",
      "payment_mode",
      "amount",
      "action",
    ].map((item) => createColumn(item)),
  ];

  const extendedColumns = (is_loan_repayment: boolean = false) => [
    {
      id: "payment_date",
      header: "Payment Date",
      filter: { type: "date" },
    },
    {
      id: "invoice_number",
      header: "Invoice Number",
      filter: true,
    },
    {
      id: "applicant_name",
      header: "Applicant Name",
      filter: true,
    },
    {
      id: "number_plate",
      header: "Number Plate",
      filter: true,
    },
    {
      id: "application_type",
      header: "Application Type",
      filter: {
        type: "select",
        options: [
          NEW_APPLICANT,
          EXISTING_APPLICANT,
          MIGRATED_APPLICANT,
          RESTRUCTURED_APPLICANT,
        ].map((item) => createSelectItem(item)),
      },
    },
    {
      id: "type_of_fee",
      header: "Type of Fee",
      filter: !is_loan_repayment && {
        type: "select",
        options: [
          ...returnObject(is_loan_repayment, [
            { name: "Loan Repayment", id: "loan_repayment" },
          ]),
          ...returnObject(!is_loan_repayment, [
            { name: "Application", id: "application" },
            { name: "Initial Loan Fee", id: "initial_loan_fee" },
          ]),
        ],
      },
    },

    {
      id: "payment_mode",
      header: "Payment Mode",
      filter: true,
    },

    {
      id: "amount",
      header: "Amount",
    },
  ];

  const tableData = (data: any) => [
    ...data?.map((item: any) => {
      const row = {
        ...item,
        invoice_for: beautify(item.type),
        status: (
          <div className="flex gap-2">
            <PaymentStatusTag status={item?.payment_status} />
            <Link className="link-with-icon" to={`/prns/view/${item?.prn?.id}`}>
              PRNS
            </Link>
          </div>
        ),
        amount: numberFormat(item?.amount),
        invoice_number: item?.invoice_number,
        generated_on: formatDate(item.created_at),
        payment_date: formatDate(item?.payment_date),
        payment_mode: beautify(item?.payment_mode),
        action: (
          <FormActions
            actions={[
              ...createViewEdit("/payments", item?.id, canEditPurchaseOrder),
              createDeleteAction(
                `${API_PAYMENTS}${item?.id}/`,
                `Payment made on ${formatDate(item.payment_date)}`,
              ),
            ]}
          />
        ),
      };
      return row;
    }),
  ];

  const viewData = (item: any) => {
    return {
      sections: [
        {
          title: "Payment Information",
          fields: {
            payment_date: formatDate(item?.payment_date),
            payment_mode: beautify(item?.prn?.payment_mode_name),
            amount: numberFormat(item?.amount),
            generated_on: formatDate(item?.created_at),
          },
        },
        {
          title: "Prn Details",
          fields: {
            amount: numberFormat(item?.prn?.amount),
            reference_number: item?.prn?.ref,
            invoice_type: beautify(item?.prn?.invoice_details?.type),
            created_by: `${item?.prn?.created_by?.first_name} ${item?.prn?.created_by?.last_name}`,
          },
        },
        {
          title: "Invoice Details",
          fields: {
            ...objectWithUnsetKeys(item?.prn?.invoice_details, [
              "id",
              "created_at",
              "updated_at",
              "created_by",
              "updated_by",
              "declaration",
              "deleted",
            ]),
            payment_status: (
              <PaymentStatusTag
                status={item?.prn?.invoice_details?.payment_status}
              />
            ),
          },
        },
      ],
    };
  };

  const tableTotalsData = (data: any) => {
    return {
      ...data,
      amount: numberFormat(data?.total_amount?.total_amount || 0),
    };
  };

  const activityFilter = "payment";

  return {
    tableColumns,
    extendedColumns,
    tableData,
    viewData,
    activityFilter,
    tableTotalsData,
  };
};
